'use client';

import { useCallback, useEffect, useState } from 'react';
import type { CountdownTime } from './types';

interface CountdownProps {
  targetDate: string;
  className?: string;
  labelClassName?: string;
  valueClassName?: string;
}

/**
 * Calculate time remaining until target date
 */
function calculateTimeRemaining(targetDate: string): CountdownTime {
  const now = new Date().getTime();
  const target = new Date(targetDate).getTime();
  const difference = target - now;

  if (difference <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  }

  return {
    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
    hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((difference % (1000 * 60)) / 1000),
  };
}

/**
 * Countdown Timer Component
 * 
 * Displays a countdown to the launch date with days, hours, minutes, seconds.
 * Updates every second.
 */
export function Countdown({
  targetDate,
  className = '',
  labelClassName = '',
  valueClassName = '',
}: CountdownProps) {
  const [timeRemaining, setTimeRemaining] = useState<CountdownTime>(() =>
    calculateTimeRemaining(targetDate)
  );
  const [mounted, setMounted] = useState(false);

  const updateCountdown = useCallback(() => {
    setTimeRemaining(calculateTimeRemaining(targetDate));
  }, [targetDate]);

  useEffect(() => {
    setMounted(true);
    updateCountdown();
    
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [updateCountdown]);

  // Prevent hydration mismatch by showing placeholder until mounted
  if (!mounted) {
    return (
      <div className={`grid grid-cols-4 gap-4 ${className}`}>
        {['Days', 'Hours', 'Minutes', 'Seconds'].map((label) => (
          <div key={label} className="text-center">
            <div className={`text-4xl md:text-5xl font-bold ${valueClassName}`}>
              --
            </div>
            <div className={`text-sm uppercase tracking-wider mt-2 ${labelClassName}`}>
              {label}
            </div>
          </div>
        ))}
      </div>
    );
  }

  const timeUnits = [
    { value: timeRemaining.days, label: 'Days' },
    { value: timeRemaining.hours, label: 'Hours' },
    { value: timeRemaining.minutes, label: 'Minutes' },
    { value: timeRemaining.seconds, label: 'Seconds' },
  ];

  return (
    <div className={`grid grid-cols-4 gap-4 ${className}`} role="timer" aria-live="polite">
      {timeUnits.map(({ value, label }) => (
        <div key={label} className="text-center">
          <div
            className={`text-4xl md:text-5xl font-bold tabular-nums ${valueClassName}`}
            aria-label={`${value} ${label}`}
          >
            {value.toString().padStart(2, '0')}
          </div>
          <div className={`text-sm uppercase tracking-wider mt-2 ${labelClassName}`}>
            {label}
          </div>
        </div>
      ))}
    </div>
  );
}
